import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X as XIcon, Loader2, Circle, MinusCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuickStartAssistant, ProposedActionCard, PlanStepView } from '../../contexts/QuickStartAssistantContext';
import { fetchWithActivity } from '../../utils/fetchWithActivity';
import DocumentDropZone, { DroppedDocument } from '../DocumentDropZone';
import DownloadFileCard from './DownloadFileCard';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface ActionCardProps {
  card: ProposedActionCard;
  /** Whether this card is still the live, actionable proposal — false once a newer card supersedes it or hydration says its server status has moved on. Cards that were confirmed/dismissed by the user render their own quiet status regardless of this flag. */
  isPending: boolean;
  /** Presentational only — the modal host gets roomier spacing than the docked bubble. */
  variant?: 'bubble' | 'modal';
  /** Called after a navigation-type action fires — lets the host close the chat widget so the destination page is visible. */
  onNavigated?: () => void;
}

/** One short line replacing the button row once a card stops being actionable — the card body above stays visible as history, this just says why there's nothing to click. */
const CardStatusFooter: React.FC<{ status: 'done' | 'dismissed' | 'superseded' }> = ({ status }) => (
  <p className="text-xs text-gray-400 dark:text-gray-500 italic">
    {status === 'done' ? 'Done' : status === 'dismissed' ? 'Dismissed' : 'Superseded'}
  </p>
);

function logCardEvent(token: string | null, tool: string, outcome: string) {
  if (!token) return;
  fetchWithActivity(`${API_BASE_URL}/api/guide/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ flowId: tool, stepId: 'action_card', outcome }),
  }).catch(() => {});
}

const ActionCard: React.FC<ActionCardProps> = ({ card, isPending, variant = 'bubble', onNavigated }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { dismissPendingCard, confirmAction, appendLocalNote, appendAssistantNote, presentFollowUpCard, activeRun, runOpenWorkflowCard, activePlan, approvePlanCard, resumePlanCard, clearIntakeSession } = useQuickStartAssistant();

  const [creatingFolder, setCreatingFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(false);
  const [savingCustomerEdit, setSavingCustomerEdit] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [renamingDocument, setRenamingDocument] = useState(false);
  const [movingDocuments, setMovingDocuments] = useState(false);
  const [savingMemory, setSavingMemory] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [cancellingTask, setCancellingTask] = useState(false);
  const [committingIntake, setCommittingIntake] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [uploadedInChat, setUploadedInChat] = useState<DroppedDocument[]>([]);

  // Seeded from the card's hydrated server status (a reload knows a card was
  // already confirmed/dismissed even though this component instance is
  // brand new) — otherwise starts null and is set locally the moment the
  // user acts, so a superseded-but-untouched card can still be told apart
  // from one this user actually confirmed or dismissed.
  const [resolution, setResolution] = useState<'confirmed' | 'dismissed' | null>(() => {
    const status = card.status;
    if (!status || status === 'proposed') return null;
    return status === 'dismissed' ? 'dismissed' : 'confirmed';
  });
  const resolvedStatus: 'idle' | 'done' | 'dismissed' | 'superseded' =
    resolution === 'confirmed' ? 'done' : resolution === 'dismissed' ? 'dismissed' : isPending ? 'idle' : 'superseded';

  const buttonSize = variant === 'modal' ? 'px-4 py-2.5 text-sm' : 'px-3 py-1.5 text-sm';

  const handleChangeSomething = () => {
    logCardEvent(token, card.tool, 'card_dismissed');
    setResolution('dismissed');
    dismissPendingCard(card.id);
  };

  // ── open_workflow ──────────────────────────────────────────────────────
  if (card.tool === 'open_workflow') {
    const { customerId, customerName, projectId, projectName, displayName, documents } = card.args as {
      workflowType: string;
      customerId: string;
      customerName?: string;
      projectId: string;
      projectName?: string;
      displayName: string;
      documents: Array<{ id: string; name: string }>;
    };

    const reportDeepLink = (result: NonNullable<typeof activeRun>['result']): string =>
      result?.savedReportId
        ? `/workflow-report?reportId=${result.savedReportId}`
        : `/customers/${customerId}/projects/${projectId}?tab=reports`;

    // Tracked in QuickStartAssistantContext (not local state) so the run
    // survives the Quick Start modal closing and reopening — ActionCard
    // unmounts on close, but the run continues server-side regardless.
    const isThisRun = activeRun?.card === card;
    const runState = isThisRun ? activeRun.status : 'idle';
    const runResult = isThisRun ? activeRun.result : undefined;
    const runError = isThisRun ? activeRun.error : undefined;

    const handleRun = () => {
      logCardEvent(token, card.tool, 'card_confirmed');
      setResolution('confirmed');
      runOpenWorkflowCard(card);
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">Run: {displayName}</p>
          <p className="text-gray-600 dark:text-gray-300">
            {customerName || 'Customer'} › {projectName || 'Folder'}
          </p>
          {documents.length > 0 && (
            <p className="text-gray-600 dark:text-gray-300">Documents: {documents.map((d) => d.name).join(', ')}</p>
          )}
        </div>

        {runState === 'idle' && resolvedStatus === 'idle' && (
          <div className="flex gap-2">
            <button onClick={handleRun} className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors`}>
              Run it
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Change something
            </button>
          </div>
        )}

        {runState === 'idle' && resolvedStatus !== 'idle' && <CardStatusFooter status={resolvedStatus} />}

        {runState === 'running' && (
          <p className="text-gray-600 dark:text-gray-300 text-sm">
            Generating the report — usually a few minutes. You can close this and check back; Arie will have it ready here when you reopen it.
          </p>
        )}

        {runState === 'completed' && runResult && (
          <button
            onClick={() => {
              navigate(reportDeepLink(runResult));
              dismissPendingCard();
              onNavigated?.();
            }}
            className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors`}
          >
            View report
          </button>
        )}

        {runState === 'failed' && (
          <div className="space-y-2">
            <p className="text-red-500 text-sm">{runError}</p>
            <button
              onClick={() => {
                navigate(`/customers/${customerId}/projects/${projectId}?tab=workflows`);
                dismissPendingCard();
                onNavigated?.();
              }}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors`}
            >
              Open Workflows tab
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── download_file ───────────────────────────────────────────────────────
  // A file Arie generated (create_report_pdf) — informational, no confirm.
  if (card.tool === 'download_file') {
    return <DownloadFileCard args={card.args as any} buttonSize={buttonSize} />;
  }

  // ── show_documents ──────────────────────────────────────────────────────
  // Informational, not a proposal: the rows come from the server's own read of
  // the folder, so there is nothing to confirm and nothing the model could have
  // mistyped. Rendered persistently (see AssistantConversation) rather than
  // disappearing once it stops being the pending card.
  if (card.tool === 'show_documents') {
    const { customerId, customerName, projectId, projectName, totalCount, truncated, documents } = card.args as {
      customerId: string;
      customerName?: string;
      projectId: string;
      projectName?: string;
      totalCount: number;
      truncated?: boolean;
      documents: Array<{ id: string; name: string; documentKind?: string | null; extractionStatus?: string }>;
    };

    const handleOpenTab = () => {
      logCardEvent(token, card.tool, 'card_confirmed');
      if (card.id) confirmAction(card.id).catch(() => {});
      navigate(`/customers/${customerId}/projects/${projectId}?tab=documents`);
      dismissPendingCard();
      onNavigated?.();
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 text-sm overflow-hidden">
          <div className="flex items-baseline justify-between gap-2 p-3 border-b border-gray-200 dark:border-gray-700">
            <p className="font-medium text-gray-800 dark:text-gray-100 truncate">
              Documents in {projectName || 'this folder'}
            </p>
            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
              {totalCount} {totalCount === 1 ? 'document' : 'documents'}
            </span>
          </div>

          {documents.length === 0 ? (
            <p className="p-3 text-gray-600 dark:text-gray-300">This folder is empty.</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {documents.map((doc) => (
                <li key={doc.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="text-gray-700 dark:text-gray-200 truncate" title={doc.name}>
                    {doc.name}
                  </span>
                  {doc.extractionStatus && doc.extractionStatus !== 'completed' && (
                    <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{doc.extractionStatus}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {truncated && (
            <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
              Showing the {documents.length} most recent of {totalCount}. Open the Documents tab to see them all.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={handleOpenTab} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
            Open Documents tab
          </button>
        </div>
      </div>
    );
  }

  // ── navigate ────────────────────────────────────────────────────────────
  if (card.tool === 'navigate') {
    const { customerId, customerName, projectId, projectName, destination, label } = card.args as {
      customerId: string;
      customerName?: string;
      projectId: string;
      projectName?: string;
      destination: 'documents' | 'workflows' | 'reports';
      label: string;
    };

    const handleGo = () => {
      logCardEvent(token, card.tool, 'card_confirmed');
      setResolution('confirmed');
      if (card.id) confirmAction(card.id).catch(() => {});
      navigate(`/customers/${customerId}/projects/${projectId}?tab=${destination}`);
      dismissPendingCard();
      onNavigated?.();
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
          <p className="font-medium text-gray-800 dark:text-gray-100">{label}</p>
          <p className="text-gray-600 dark:text-gray-300">
            {customerName || 'Customer'} › {projectName || 'Folder'}
          </p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button onClick={handleGo} className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors`}>
              Go
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── start_upload ────────────────────────────────────────────────────────
  if (card.tool === 'start_upload') {
    const { customerId, customerName, projectId, projectName } = card.args as {
      customerId: string;
      customerName?: string;
      projectId: string;
      projectName?: string;
    };

    // First upload confirms the card (once — SERVER_EXECUTED_TOOLS doesn't
    // include start_upload, so this just records that the user acted on it,
    // same as the old navigate-away button did). Later uploads in the same
    // card session hit the same call again and 409 harmlessly.
    const handleDocumentsAdded = (docs: DroppedDocument[]) => {
      logCardEvent(token, card.tool, 'card_confirmed');
      if (card.id) confirmAction(card.id).catch(() => {});
      setUploadedInChat((prev) => [...prev, ...docs]);
      appendAssistantNote(`Uploaded ${docs.length} document${docs.length === 1 ? '' : 's'}: ${docs.map((d) => d.name).join(', ')}.`);
    };

    const handleDone = () => {
      setResolution('confirmed');
      dismissPendingCard();
    };

    const handleOpenTabInstead = () => {
      logCardEvent(token, card.tool, 'card_confirmed');
      setResolution('confirmed');
      if (card.id) confirmAction(card.id).catch(() => {});
      navigate(`/customers/${customerId}/projects/${projectId}?tab=documents`);
      dismissPendingCard();
      onNavigated?.();
    };

    if (resolvedStatus !== 'idle') {
      return (
        <div className="mt-2 space-y-2">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
            <p className="font-medium text-gray-800 dark:text-gray-100">Upload documents</p>
            <p className="text-gray-600 dark:text-gray-300">{customerName || 'Customer'} › {projectName || 'Folder'}</p>
          </div>
          <CardStatusFooter status={resolvedStatus} />
        </div>
      );
    }

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-2">
          <div>
            <p className="font-medium text-gray-800 dark:text-gray-100">Upload documents</p>
            <p className="text-gray-600 dark:text-gray-300">{customerName || 'Customer'} › {projectName || 'Folder'}</p>
          </div>
          <DocumentDropZone
            customerId={customerId}
            projectId={projectId}
            onDocumentsAdded={handleDocumentsAdded}
            maxFiles={10}
            maxFileSize={50}
          />
        </div>
        <div className="flex gap-2">
          {uploadedInChat.length > 0 ? (
            <button onClick={handleDone} className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors`}>
              Done
            </button>
          ) : (
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          )}
          <button onClick={handleOpenTabInstead} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
            Open Documents tab instead
          </button>
        </div>
      </div>
    );
  }

  // ── create_folder ───────────────────────────────────────────────────────
  // Executes server-side on confirm (Phase 3a) — no more inline fetch here.
  if (card.tool === 'create_folder') {
    const { customerId, customerName, name, parentName } = card.args as { customerId: string; customerName?: string; name: string; parentId?: string | null; parentName?: string };

    const handleCreate = async () => {
      if (!card.id) return;
      setCreatingFolder(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, result, error } = await confirmAction(card.id);
        if (status !== 'completed' || !result) throw new Error(error || 'Failed to create folder');
        setResolution('confirmed');
        dismissPendingCard();
        // Stay in chat: the client already has everything a start_upload
        // card needs (the real projectId this confirm just returned), so
        // show it directly instead of navigating away and waiting for the
        // model to re-discover the new folder in a later turn.
        presentFollowUpCard(`Created "${result.title || name}".`, {
          tool: 'start_upload',
          args: { customerId, customerName, projectId: result.projectId, projectName: result.title || name },
        });
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't create that folder — please try again from the customer page.`);
      } finally {
        setCreatingFolder(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
          <p className="font-medium text-gray-800 dark:text-gray-100">Create folder: {name}</p>
          <p className="text-gray-600 dark:text-gray-300">
            Under: {customerName || 'this customer'}{parentName ? ` › ${parentName}` : ''}
          </p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={creatingFolder}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {creatingFolder ? 'Creating…' : 'Create folder'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── rename_folder ───────────────────────────────────────────────────────
  if (card.tool === 'rename_folder') {
    const { oldName, newName } = card.args as { projectId: string; oldName: string; newName: string };

    const handleRename = async () => {
      if (!card.id) return;
      setRenamingFolder(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to rename folder');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Renamed "${oldName}" to "${newName}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't rename that folder — please try again.`);
      } finally {
        setRenamingFolder(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">Rename folder</p>
          <p className="text-gray-600 dark:text-gray-300 truncate" title={oldName}>{oldName}</p>
          <p className="text-gray-400 dark:text-gray-500">→</p>
          <p className="text-gray-600 dark:text-gray-300 truncate" title={newName}>{newName}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleRename}
              disabled={renamingFolder}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {renamingFolder ? 'Renaming…' : 'Rename it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── delete_folder ───────────────────────────────────────────────────────
  // Destructive — red confirm button, never task-allowed (see TASK_ALLOWED_TOOLS
  // server-side). Documents inside are detached, not deleted, so the card says so.
  if (card.tool === 'delete_folder') {
    const { customerName, projectName, documentCount, childFolderCount } = card.args as {
      projectId: string;
      customerName?: string;
      projectName?: string;
      documentCount: number;
      childFolderCount: number;
    };

    const handleDelete = async () => {
      if (!card.id) return;
      setDeletingFolder(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to delete folder');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Deleted "${projectName || 'the folder'}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't delete that folder — please try again.`);
      } finally {
        setDeletingFolder(false);
      }
    };

    const contents: string[] = [];
    if (documentCount > 0) contents.push(`${documentCount} document${documentCount === 1 ? '' : 's'} (kept, unfiled)`);
    if (childFolderCount > 0) contents.push(`${childFolderCount} sub-folder${childFolderCount === 1 ? '' : 's'}`);

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-red-200 dark:border-red-900 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">Delete folder: {projectName || 'this folder'}</p>
          <p className="text-gray-600 dark:text-gray-300">{customerName || 'this customer'}</p>
          <p className="text-gray-500 dark:text-gray-400 text-xs">
            {contents.length > 0 ? `Contains ${contents.join(', ')}.` : 'This folder is empty.'}
          </p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={deletingFolder}
              className={`${buttonSize} bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-60`}
            >
              {deletingFolder ? 'Deleting…' : 'Delete it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Keep it
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── delete_document ─────────────────────────────────────────────────────
  if (card.tool === 'delete_document') {
    const { documentName } = card.args as { documentId: string; documentName: string };

    const handleDelete = async () => {
      if (!card.id) return;
      setDeletingDocument(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to delete document');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Deleted "${documentName}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't delete that document — please try again.`);
      } finally {
        setDeletingDocument(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-red-200 dark:border-red-900 p-3 text-sm">
          <p className="font-medium text-gray-800 dark:text-gray-100">Delete document</p>
          <p className="text-gray-600 dark:text-gray-300 truncate" title={documentName}>{documentName}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={deletingDocument}
              className={`${buttonSize} bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-60`}
            >
              {deletingDocument ? 'Deleting…' : 'Delete it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Keep it
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── edit_customer ───────────────────────────────────────────────────────
  if (card.tool === 'edit_customer') {
    const { before, after } = card.args as {
      customerId: string;
      before: { name: string; customerType: string; lineType: string };
      after: { name?: string; customerType?: string; lineType?: string };
    };

    const handleSave = async () => {
      if (!card.id) return;
      setSavingCustomerEdit(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, result, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to update customer');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Updated "${result?.name || before.name}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't update that customer — please try again.`);
      } finally {
        setSavingCustomerEdit(false);
      }
    };

    const rows: Array<{ label: string; from: string; to: string }> = [];
    if (after.name !== undefined && after.name !== before.name) rows.push({ label: 'Name', from: before.name, to: after.name });
    if (after.customerType !== undefined && after.customerType !== before.customerType) rows.push({ label: 'Type', from: before.customerType, to: after.customerType });
    if (after.lineType !== undefined && after.lineType !== before.lineType) rows.push({ label: 'Line', from: before.lineType, to: after.lineType });

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1.5">
          <p className="font-medium text-gray-800 dark:text-gray-100">Update {before.name}</p>
          {rows.map((row) => (
            <p key={row.label} className="text-gray-600 dark:text-gray-300">
              {row.label}: <span className="line-through text-gray-400 dark:text-gray-500">{row.from}</span> → {row.to}
            </p>
          ))}
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={savingCustomerEdit}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {savingCustomerEdit ? 'Saving…' : 'Save it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── create_customer ────────────────────────────────────────────────────
  if (card.tool === 'create_customer') {
    const { name, customerType, lineType } = card.args as { name: string; customerType: 'customer' | 'prospect'; lineType: 'commercial' | 'personal' };

    const handleCreate = async () => {
      if (!card.id) return;
      setCreatingCustomer(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, result, error } = await confirmAction(card.id);
        if (status !== 'completed' || !result) throw new Error(error || 'Failed to create customer');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Created "${result.name || name}".`);
        // Picks the rest of a compound request back up on its own (e.g. "create
        // a customer AND a folder for them") instead of leaving the user to
        // notice and re-ask — see routes/assistant.ts's CONTINUABLE_TOOLS.
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't create that customer — please try again from the customer list.`);
      } finally {
        setCreatingCustomer(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
          <p className="font-medium text-gray-800 dark:text-gray-100">Create {customerType === 'prospect' ? 'prospect' : 'customer'}: {name}</p>
          <p className="text-gray-600 dark:text-gray-300">{lineType === 'personal' ? 'Personal lines' : 'Commercial lines'}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={creatingCustomer}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {creatingCustomer ? 'Creating…' : 'Create it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── commit_intake ──────────────────────────────────────────────────────
  if (card.tool === 'commit_intake') {
    const { customerName, newCustomerName, customerType, projectName, parentName, documentCount, thenRun } = card.args as {
      customerName?: string;
      newCustomerName?: string;
      customerType: 'customer' | 'prospect';
      projectName: string;
      parentName?: string;
      documentCount: number;
      thenRun?: { workflowType: string; documentIds: string[]; displayName: string };
    };

    const handleCommit = async () => {
      if (!card.id) return;
      setCommittingIntake(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, result, error } = await confirmAction(card.id);
        if (status === 'failed' || !result) throw new Error(error || 'Failed to file these documents');
        setResolution('confirmed');
        dismissPendingCard();
        clearIntakeSession();
        const filedNote = `Filed ${documentCount} document${documentCount === 1 ? '' : 's'} into ${result.customerName || customerName || newCustomerName} › "${result.projectName || projectName}".`;
        if (status === 'executing' && thenRun) {
          appendLocalNote(`${filedNote} Running ${thenRun.displayName} now — I'll drop a link here when it's ready.`);
        } else if (result.thenRunError) {
          appendLocalNote(`${filedNote} ${thenRun?.displayName || 'The run'} couldn't start: ${result.thenRunError}`);
        } else {
          appendLocalNote(filedNote);
        }
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't file those documents — please try again.`);
      } finally {
        setCommittingIntake(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">
            {customerName ? `Use existing: ${customerName}` : `Create ${customerType === 'prospect' ? 'prospect' : 'customer'} "${newCustomerName}"`}
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            + folder "{projectName}"{parentName ? ` › ${parentName}` : ''}
          </p>
          <p className="text-gray-600 dark:text-gray-300">{documentCount} document{documentCount === 1 ? '' : 's'}</p>
          {thenRun && (
            <p className="text-gray-600 dark:text-gray-300">then run <span className="font-medium">{thenRun.displayName}</span></p>
          )}
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleCommit}
              disabled={committingIntake}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {committingIntake ? 'Filing…' : thenRun ? 'File and run it' : 'File documents'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── rename_document ────────────────────────────────────────────────────
  if (card.tool === 'rename_document') {
    const { oldName, newName } = card.args as { documentId: string; oldName: string; newName: string };

    const handleRename = async () => {
      if (!card.id) return;
      setRenamingDocument(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to rename document');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Renamed "${oldName}" to "${newName}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't rename that document — please try again.`);
      } finally {
        setRenamingDocument(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">Rename document</p>
          <p className="text-gray-600 dark:text-gray-300 truncate" title={oldName}>{oldName}</p>
          <p className="text-gray-400 dark:text-gray-500">→</p>
          <p className="text-gray-600 dark:text-gray-300 truncate" title={newName}>{newName}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleRename}
              disabled={renamingDocument}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {renamingDocument ? 'Renaming…' : 'Rename it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── move_documents ─────────────────────────────────────────────────────
  if (card.tool === 'move_documents') {
    const { documents, targetProjectName, customerName } = card.args as {
      documentIds: string[];
      documents: Array<{ id: string; name: string }>;
      targetProjectId: string;
      targetProjectName?: string;
      customerName?: string;
    };

    const handleMove = async () => {
      if (!card.id) return;
      setMovingDocuments(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to move one or more documents');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Moved ${documents.length} document${documents.length === 1 ? '' : 's'} to "${targetProjectName || 'the folder'}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't move those documents — please try again.`);
      } finally {
        setMovingDocuments(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">Move {documents.length} document{documents.length === 1 ? '' : 's'}</p>
          <p className="text-gray-600 dark:text-gray-300">{documents.map((d) => d.name).join(', ')}</p>
          <p className="text-gray-600 dark:text-gray-300">To: {customerName ? `${customerName} › ` : ''}{targetProjectName || 'this folder'}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleMove}
              disabled={movingDocuments}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {movingDocuments ? 'Moving…' : 'Move it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── save_memory ─────────────────────────────────────────────────────────
  if (card.tool === 'save_memory') {
    const { content } = card.args as { content: string };

    const handleSaveMemory = async () => {
      if (!card.id) return;
      setSavingMemory(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to save that');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Remembered: ${content}`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't save that — please try again.`);
      } finally {
        setSavingMemory(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">Remember this?</p>
          <p className="text-gray-600 dark:text-gray-300">{content}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleSaveMemory}
              disabled={savingMemory}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {savingMemory ? 'Saving…' : 'Save it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              No thanks
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── create_task ─────────────────────────────────────────────────────────
  if (card.tool === 'create_task') {
    const { description, trigger, action } = card.args as {
      description: string;
      trigger: { type: 'extraction_complete' | 'schedule'; projectId?: string; everyMinutes?: number };
      action: { tool: string; args: Record<string, any> };
    };

    const triggerLabel = trigger.type === 'schedule'
      ? `Runs every ${trigger.everyMinutes} minutes`
      : 'Runs once, when extractions in that folder finish';
    const actionLabel = action.tool === 'propose_plan'
      ? `Then: ${(action.args.steps as Array<{ description: string }> | undefined)?.map((s) => s.description).join(', ') || action.args.title}`
      : `Then: ${action.tool.replace(/_/g, ' ')}`;

    const handleCreateTask = async () => {
      if (!card.id) return;
      setCreatingTask(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to set up that task');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Set up: "${description}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't set that up — please try again.`);
      } finally {
        setCreatingTask(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">{description}</p>
          <p className="text-gray-500 dark:text-gray-400 text-xs">{triggerLabel}</p>
          <p className="text-gray-500 dark:text-gray-400 text-xs truncate">{actionLabel}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleCreateTask}
              disabled={creatingTask}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {creatingTask ? 'Setting up…' : 'Set it up'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              No thanks
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── cancel_task ─────────────────────────────────────────────────────────
  if (card.tool === 'cancel_task') {
    const { description } = card.args as { taskId: string; description: string };

    const handleCancelTask = async () => {
      if (!card.id) return;
      setCancellingTask(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, error } = await confirmAction(card.id);
        if (status !== 'completed') throw new Error(error || 'Failed to cancel that task');
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Cancelled "${description}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : `I couldn't cancel that — please try again.`);
      } finally {
        setCancellingTask(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">Cancel task</p>
          <p className="text-gray-600 dark:text-gray-300">{description}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleCancelTask}
              disabled={cancellingTask}
              className={`${buttonSize} bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-60`}
            >
              {cancellingTask ? 'Cancelling…' : 'Cancel it'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Keep it
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── open_report ─────────────────────────────────────────────────────────
  if (card.tool === 'open_report') {
    const { reportId, label } = card.args as { reportId: string; title: string; label: string };

    const handleOpen = () => {
      logCardEvent(token, card.tool, 'card_confirmed');
      setResolution('confirmed');
      if (card.id) confirmAction(card.id).catch(() => {});
      navigate(`/workflow-report?reportId=${reportId}`);
      dismissPendingCard();
      onNavigated?.();
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
          <p className="font-medium text-gray-800 dark:text-gray-100">{label}</p>
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button onClick={handleOpen} className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors`}>
              Open report
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── save_as_report ──────────────────────────────────────────────────────
  if (card.tool === 'save_as_report') {
    const { title, customerName, projectName } = card.args as {
      title: string;
      customerName?: string;
      projectName?: string;
    };

    const handleSave = async () => {
      if (!card.id) return;
      setSavingReport(true);
      logCardEvent(token, card.tool, 'card_confirmed');
      try {
        const { status, result, error } = await confirmAction(card.id);
        if (status !== 'completed' || !result) throw new Error(error || "Failed to save that as a report");
        setResolution('confirmed');
        dismissPendingCard();
        appendLocalNote(`Saved as report "${result.title || title}".`);
      } catch (err) {
        appendAssistantNote(err instanceof Error ? err.message : "I couldn't save that as a report — please try again.");
      } finally {
        setSavingReport(false);
      }
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm space-y-1">
          <p className="font-medium text-gray-800 dark:text-gray-100">Save as report</p>
          <p className="text-gray-600 dark:text-gray-300">"{title}"</p>
          {(customerName || projectName) && (
            <p className="text-gray-600 dark:text-gray-300">
              {customerName}{customerName && projectName ? ' › ' : ''}{projectName}
            </p>
          )}
        </div>
        {resolvedStatus === 'idle' ? (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={savingReport}
              className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-60`}
            >
              {savingReport ? 'Saving…' : 'Save as report'}
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        ) : (
          <CardStatusFooter status={resolvedStatus} />
        )}
      </div>
    );
  }

  // ── propose_plan ────────────────────────────────────────────────────────
  // Renders as an ordered checklist; steps run sequentially server-side once
  // approved, streaming live status into activePlan (Phase 3b). Tracked in
  // context (not local state) for the same reason as activeRun — the card
  // component unmounts on modal close, the run doesn't.
  if (card.tool === 'propose_plan') {
    const { title, steps } = card.args as {
      title: string;
      steps: Array<{ tool: string; args: Record<string, any>; description: string }>;
    };

    const isThisPlan = activePlan?.card === card;
    const planStatus = isThisPlan ? activePlan.status : 'idle';
    const liveSteps: PlanStepView[] = isThisPlan ? activePlan.steps : steps.map((s) => ({ ...s, status: 'pending' }));
    const pausedStep = planStatus === 'paused' ? liveSteps.find((s) => s.status === 'paused') : undefined;

    const handleApprove = () => {
      logCardEvent(token, card.tool, 'card_confirmed');
      setResolution('confirmed');
      approvePlanCard(card);
    };

    const stepIcon = (status: string) => {
      if (status === 'completed') return <Check className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />;
      if (status === 'failed') return <XIcon className="w-4 h-4 text-red-500 shrink-0" />;
      if (status === 'running') return <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />;
      if (status === 'paused') return <Circle className="w-4 h-4 text-amber-500 shrink-0" />;
      if (status === 'skipped') return <MinusCircle className="w-4 h-4 text-gray-400 shrink-0" />;
      return <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />;
    };

    return (
      <div className="mt-2 space-y-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 text-sm overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <p className="font-medium text-gray-800 dark:text-gray-100">{title}</p>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {liveSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-2 px-3 py-2">
                {stepIcon(step.status)}
                <div className="min-w-0">
                  <p className={`truncate ${step.status === 'skipped' ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-200'}`}>
                    {step.description}
                  </p>
                  {step.status === 'failed' && step.error && (
                    <p className="text-xs text-red-500 mt-0.5">{step.error}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {planStatus === 'idle' && resolvedStatus === 'idle' && (
          <div className="flex gap-2">
            <button onClick={handleApprove} className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors`}>
              Approve plan
            </button>
            <button onClick={handleChangeSomething} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
              Not now
            </button>
          </div>
        )}

        {planStatus === 'idle' && resolvedStatus !== 'idle' && <CardStatusFooter status={resolvedStatus} />}

        {planStatus === 'running' && (
          <p className="text-gray-600 dark:text-gray-300 text-sm">Working through the plan…</p>
        )}

        {planStatus === 'paused' && pausedStep && (
          <div className="space-y-2">
            <p className="text-gray-600 dark:text-gray-300 text-sm">Drop the documents below, then continue.</p>
            {pausedStep.args?.customerId && pausedStep.args?.projectId && (
              <DocumentDropZone
                customerId={pausedStep.args.customerId}
                projectId={pausedStep.args.projectId}
                onDocumentsAdded={(docs) => setUploadedInChat((prev) => [...prev, ...docs])}
                maxFiles={10}
                maxFileSize={50}
              />
            )}
            <div className="flex gap-2">
              {pausedStep.args?.customerId && pausedStep.args?.projectId && (
                <button
                  onClick={() => navigate(`/customers/${pausedStep.args.customerId}/projects/${pausedStep.args.projectId}?tab=documents`)}
                  className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                >
                  Open Documents tab instead
                </button>
              )}
              <button
                onClick={() => { logCardEvent(token, card.tool, 'card_confirmed'); resumePlanCard(); }}
                className={`${buttonSize} bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors`}
              >
                I've uploaded — continue
              </button>
            </div>
          </div>
        )}

        {planStatus === 'failed' && (
          <button onClick={() => dismissPendingCard()} className={`${buttonSize} text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
            Dismiss
          </button>
        )}

        {planStatus === 'completed' && (
          <p className="text-green-600 dark:text-green-400 text-sm">Plan complete.</p>
        )}
      </div>
    );
  }

  return null;
};

export default ActionCard;
